import type { IAttendanceDashboardStateDetail } from '@src/models/interfaces/attendanceDashboard.interface';
import type {
  IWorkJourneyManagementAttentionItem,
  IWorkJourneyManagementCalendarCell,
  IWorkJourneyManagementCalendarDayConflictItem,
  IWorkJourneyManagementCalendarDayIndicator,
  IWorkJourneyManagementCalendarMonthSummary,
  IWorkJourneyManagementJourneyListItem,
  TWorkJourneyManagementJourneyMonthStatusType,
} from '@src/models/interfaces/workJourneyManagement.interface';

import {
  ATTENDANCE_DASHBOARD_STATES,
  WORK_JOURNEY_MANAGEMENT_JOURNEY_FILTER_OPTIONS_LIMIT,
} from '../constants';

export const WORK_JOURNEY_MANAGEMENT_FILTER_ALL = 'all';

/**
 * Status axis shared by the three tabs. It reuses the journey month status
 * types so the filter labels match the badges and sidebar counters already
 * rendered (`workJourney.management.journeyStatus.*`).
 */
export type TWorkJourneyManagementFilterStatus = Exclude<
  TWorkJourneyManagementJourneyMonthStatusType,
  'on_track'
>;

export const WORK_JOURNEY_MANAGEMENT_FILTER_STATUSES: TWorkJourneyManagementFilterStatus[] = [
  'below_minimum',
  'scheduled_absences',
  'staffing_shortage',
  'rejections',
];

export interface IWorkJourneyManagementFilters {
  status: TWorkJourneyManagementFilterStatus | typeof WORK_JOURNEY_MANAGEMENT_FILTER_ALL;
  /** Work journey config id; applied server-side via context `journeyFilter`. */
  journeyId: string;
  userId: string;
  shift: string;
}

export interface IWorkJourneyManagementFilterOption {
  value: string;
  label: string;
}

export const DEFAULT_WORK_JOURNEY_MANAGEMENT_FILTERS: IWorkJourneyManagementFilters = {
  status: WORK_JOURNEY_MANAGEMENT_FILTER_ALL,
  journeyId: WORK_JOURNEY_MANAGEMENT_FILTER_ALL,
  userId: WORK_JOURNEY_MANAGEMENT_FILTER_ALL,
  shift: WORK_JOURNEY_MANAGEMENT_FILTER_ALL,
};

const STATE_TO_FILTER_STATUS: Record<string, TWorkJourneyManagementFilterStatus> = {
  [ATTENDANCE_DASHBOARD_STATES.UNDERSTAFFED_DUE_TO_ABSENCES]: 'below_minimum',
  [ATTENDANCE_DASHBOARD_STATES.USERS_UNAVAILABLE]: 'scheduled_absences',
  [ATTENDANCE_DASHBOARD_STATES.REJECTED_SHIFT_CHANGE]: 'rejections',
  [ATTENDANCE_DASHBOARD_STATES.INSUFFICIENT_ASSIGNED_MEMBERS]: 'staffing_shortage',
};

/** Calendar chips use the indicator vocabulary, which drops staffing shortage. */
const FILTER_STATUS_TO_INDICATOR_TYPE: Record<
  TWorkJourneyManagementFilterStatus,
  IWorkJourneyManagementCalendarDayIndicator['type'] | undefined
> = {
  below_minimum: 'below_minimum',
  scheduled_absences: 'scheduled_absences',
  rejections: 'rejections',
  staffing_shortage: undefined,
};

const INDICATOR_TYPE_ORDER: IWorkJourneyManagementCalendarDayIndicator['type'][] = [
  'below_minimum',
  'scheduled_absences',
  'rejections',
];

/**
 * Client-side axes for the attention tab. Journey is applied on the server
 * (`workJourneyConfigId`). Calendar / journeys resolve `userId` server-side.
 */
export const hasClientSideFilters = (filters: IWorkJourneyManagementFilters): boolean =>
  filters.status !== WORK_JOURNEY_MANAGEMENT_FILTER_ALL ||
  filters.userId !== WORK_JOURNEY_MANAGEMENT_FILTER_ALL ||
  filters.shift !== WORK_JOURNEY_MANAGEMENT_FILTER_ALL;

/**
 * Journeys tab: user membership is resolved server-side (`userId` on listJourneys),
 * so it must not force an unpaginated client pass. Status + shift stay client-side.
 */
export const hasClientSideFiltersForJourneys = (
  filters: IWorkJourneyManagementFilters,
): boolean =>
  filters.status !== WORK_JOURNEY_MANAGEMENT_FILTER_ALL ||
  filters.shift !== WORK_JOURNEY_MANAGEMENT_FILTER_ALL;

/**
 * Calendar tab: same as journeys — `userId` is plantilla membership on
 * listCalendar (holidays always returned). Status + shift stay client-side.
 */
export const hasClientSideFiltersForCalendar = hasClientSideFiltersForJourneys;

/** Any filter that should show Clear / “filtered” empty states, including journey. */
export const hasActiveFilters = (filters: IWorkJourneyManagementFilters): boolean =>
  hasClientSideFilters(filters) || filters.journeyId !== WORK_JOURNEY_MANAGEMENT_FILTER_ALL;

const getDetailUserIds = (details?: IAttendanceDashboardStateDetail): string[] => {
  if (!details) return [];
  return [
    ...(details.absentUsers ?? []).map(user => user.employeeId),
    ...(details.unavailableUsers ?? []).map(user => user.employeeId),
    ...(details.rejectedShiftChanges ?? []).map(change => change.employeeId),
  ].filter(Boolean);
};

const matchesStatus = (state: string, filters: IWorkJourneyManagementFilters) =>
  filters.status === WORK_JOURNEY_MANAGEMENT_FILTER_ALL ||
  STATE_TO_FILTER_STATUS[state] === filters.status;

const matchesUser = (
  details: IAttendanceDashboardStateDetail | undefined,
  filters: IWorkJourneyManagementFilters,
) =>
  filters.userId === WORK_JOURNEY_MANAGEMENT_FILTER_ALL ||
  getDetailUserIds(details).includes(filters.userId);

const matchesShift = (
  workJourneyConfigId: string,
  filters: IWorkJourneyManagementFilters,
  shiftByJourneyId: Record<string, string>,
) =>
  filters.shift === WORK_JOURNEY_MANAGEMENT_FILTER_ALL ||
  shiftByJourneyId[workJourneyConfigId] === filters.shift;

/**
 * Attention rows and calendar conflicts share the same shape, so one predicate
 * keeps both tabs filtering by the exact same rules.
 */
export const matchesConflictFilters = (
  item: IWorkJourneyManagementAttentionItem | IWorkJourneyManagementCalendarDayConflictItem,
  filters: IWorkJourneyManagementFilters,
  shiftByJourneyId: Record<string, string> = {},
): boolean =>
  matchesStatus(item.state, filters) &&
  matchesUser(item.details, filters) &&
  matchesShift(item.workJourneyConfigId, filters, shiftByJourneyId);

export const filterAttentionItems = (
  items: IWorkJourneyManagementAttentionItem[],
  filters: IWorkJourneyManagementFilters,
  shiftByJourneyId: Record<string, string> = {},
): IWorkJourneyManagementAttentionItem[] => {
  if (!hasClientSideFilters(filters)) return items;

  return items.filter(item => matchesConflictFilters(item, filters, shiftByJourneyId));
};

/** Mirrors the backend counters so chips keep the grain of the visible conflicts. */
const buildCellCounters = (conflicts: IWorkJourneyManagementCalendarDayConflictItem[]) => {
  const countsByType = new Map<IWorkJourneyManagementCalendarDayIndicator['type'], number>();
  const affectedJourneyIds = new Set<string>();

  conflicts.forEach(conflict => {
    affectedJourneyIds.add(conflict.workJourneyConfigId);
    const status = STATE_TO_FILTER_STATUS[conflict.state];
    const type = status ? FILTER_STATUS_TO_INDICATOR_TYPE[status] : undefined;
    if (!type) return;
    countsByType.set(type, (countsByType.get(type) ?? 0) + 1);
  });

  const indicators = INDICATOR_TYPE_ORDER.filter(type => (countsByType.get(type) ?? 0) > 0).map(
    type => ({ type, count: countsByType.get(type) as number }),
  );

  return { indicators, journeyCount: affectedJourneyIds.size };
};

const resolveHighlight = (
  cell: IWorkJourneyManagementCalendarCell,
  indicators: IWorkJourneyManagementCalendarDayIndicator[],
): IWorkJourneyManagementCalendarCell['highlight'] => {
  if (indicators.some(indicator => indicator.type === 'below_minimum')) return 'critical';
  if (indicators.some(indicator => indicator.type === 'rejections')) return 'rejections';
  if (indicators.some(indicator => indicator.type === 'scheduled_absences')) return 'warning';
  if (cell.holidayLabel) return 'holiday';
  return cell.isToday ? 'today' : 'normal';
};

export const filterCalendarCells = (
  cells: IWorkJourneyManagementCalendarCell[],
  filters: IWorkJourneyManagementFilters,
  shiftByJourneyId: Record<string, string> = {},
): IWorkJourneyManagementCalendarCell[] => {
  // User membership is server-side; do not drop holiday days for a user filter.
  if (!hasClientSideFiltersForCalendar(filters)) return cells;

  return cells.map(cell => {
    const conflicts = (cell.conflicts ?? []).filter(
      conflict =>
        matchesStatus(conflict.state, filters) &&
        matchesShift(conflict.workJourneyConfigId, filters, shiftByJourneyId),
    );
    const { indicators, journeyCount } = buildCellCounters(conflicts);

    return {
      ...cell,
      conflicts,
      indicators,
      journeyCount,
      // Preserves `holiday` / `today` when status/shift clear every conflict.
      highlight: resolveHighlight(cell, indicators),
    };
  });
};

/** Sidebar counts days with issues, so it is rebuilt from the visible cells. */
export const buildCalendarSummaryFromCells = (
  cells: IWorkJourneyManagementCalendarCell[],
  summary: IWorkJourneyManagementCalendarMonthSummary,
): IWorkJourneyManagementCalendarMonthSummary => {
  const monthCells = cells.filter(cell => cell.isCurrentMonth);
  const countDays = (type: IWorkJourneyManagementCalendarDayIndicator['type']) =>
    monthCells.filter(cell => cell.indicators.some(indicator => indicator.type === type)).length;

  return {
    ...summary,
    issues: [
      { type: 'below_minimum', count: countDays('below_minimum') },
      { type: 'scheduled_absences', count: countDays('scheduled_absences') },
      { type: 'rejections', count: countDays('rejections') },
      { type: 'holiday', count: monthCells.filter(cell => Boolean(cell.holidayLabel)).length },
    ],
  };
};

/**
 * Journey rows have no per-row user list; membership is filtered server-side
 * via `listJourneys.userId`. Only status and shift apply here.
 */
export const filterJourneyItems = (
  items: IWorkJourneyManagementJourneyListItem[],
  filters: IWorkJourneyManagementFilters,
): IWorkJourneyManagementJourneyListItem[] => {
  if (!hasClientSideFiltersForJourneys(filters)) return items;

  return items.filter(item => {
    const matchesStatusFilter =
      filters.status === WORK_JOURNEY_MANAGEMENT_FILTER_ALL ||
      item.monthStatus.some(status => status.type === filters.status);
    const matchesShiftFilter =
      filters.shift === WORK_JOURNEY_MANAGEMENT_FILTER_ALL ||
      item.shiftDescription === filters.shift;

    return matchesStatusFilter && matchesShiftFilter;
  });
};

export const buildShiftMap = (
  items: IWorkJourneyManagementJourneyListItem[],
): Record<string, string> =>
  items.reduce<Record<string, string>>((map, item) => {
    if (item.shiftDescription) {
      map[item.workJourneyConfig.id] = item.shiftDescription;
    }
    return map;
  }, {});

export const buildShiftOptions = (
  items: IWorkJourneyManagementJourneyListItem[],
): IWorkJourneyManagementFilterOption[] => {
  const shifts = new Set<string>();
  items.forEach(item => {
    if (item.shiftDescription) shifts.add(item.shiftDescription);
  });

  return Array.from(shifts)
    .sort((left, right) => left.localeCompare(right))
    .map(shift => ({ value: shift, label: shift }));
};

/**
 * Caps journey Select options for the filter panel. Search matches by name
 * against the full list, then returns at most `limit` rows. The selected
 * journey is always kept visible even when it falls outside the top matches.
 */
export const getLimitedJourneyFilterOptions = (
  options: IWorkJourneyManagementFilterOption[],
  search: string,
  selectedId: string,
  limit: number = WORK_JOURNEY_MANAGEMENT_JOURNEY_FILTER_OPTIONS_LIMIT,
): IWorkJourneyManagementFilterOption[] => {
  const query = search.trim().toLowerCase();
  const matched = query
    ? options.filter(option => option.label.toLowerCase().includes(query))
    : options;

  const limited = matched.slice(0, limit);
  if (
    selectedId === WORK_JOURNEY_MANAGEMENT_FILTER_ALL ||
    limited.some(option => option.value === selectedId)
  ) {
    return limited;
  }

  const selected = options.find(option => option.value === selectedId);
  if (!selected) return limited;

  return [selected, ...limited.filter(option => option.value !== selectedId)].slice(0, limit);
};
